import type { UnlayerMergeTags } from './UnlayerDesigner'
import type { VariableCatalogEntry, VariableCatalogGroup, VariableSurface } from '@/types/lookups'

/**
 * Email template slot markers (ORB-289). These are NOT web variables and deliberately do not
 * come from the ORB-466 catalog: the catalog answers "what data does this web have", whereas
 * these are template mechanics. `{{BODY}}` marks where the bound copy's body lands, and the
 * CTA pair is supplied per copy at send time.
 *
 * They still belong in the reference panel. The panel claims to be the complete list of what
 * works here, and silently omitting three tokens the template genuinely supports would
 * recreate the exact "typed a token, got raw text" failure ORB-466 exists to close.
 */
export const TEMPLATE_SLOT_VARIABLES: VariableCatalogEntry[] = [
    {
        Token: '{{BODY}}',
        Name: 'BODY',
        Label: 'Email body',
        Description: 'where the bound email copy drops its body',
        SampleValue: 'Filled from the email copy at send time',
        Available: true,
        UnavailableReason: null,
        Surfaces: ['EmailBody'],
    },
    {
        Token: '{{CTA_TEXT}}',
        Name: 'CTA_TEXT',
        Label: 'CTA button text',
        Description: 'the call to action button label',
        SampleValue: 'Save your spot',
        Available: true,
        UnavailableReason: null,
        Surfaces: ['EmailBody'],
    },
    {
        Token: '{{CTA_URL}}',
        Name: 'CTA_URL',
        Label: 'CTA destination URL',
        Description: 'where the call to action button points',
        SampleValue: 'https://example.com/register',
        Available: true,
        UnavailableReason: null,
        Surfaces: ['EmailBody'],
    },
]

export const TEMPLATE_SLOT_GROUP: VariableCatalogGroup = {
    Label: 'Template slots',
    Variables: TEMPLATE_SLOT_VARIABLES,
}

/**
 * Projects the web's variable catalog into Unlayer's merge-tag shape.
 *
 * This is what makes in-body insertion work. Unlayer runs in a cross-origin iframe and
 * exposes no cursor API, so we cannot insert into the body ourselves — but Unlayer's own
 * merge-tag menu inserts at the cursor natively. Feeding it the catalog means that menu
 * lists exactly the tokens that resolve for this web, with this web's real values, instead
 * of the hardcoded set it carried before ORB-466.
 *
 * Unavailable variables are included on purpose: the menu should show what exists, and a
 * variable that is empty today (no agenda items yet) may well be filled before send.
 */
export function toUnlayerMergeTags(
    groups: readonly VariableCatalogGroup[],
    surface: VariableSurface,
    extraVariables: readonly VariableCatalogEntry[] = [],
): UnlayerMergeTags {
    const tags: UnlayerMergeTags = {}
    const all = [...groups.flatMap(group => group.Variables), ...extraVariables]

    for (const variable of all) {
        if (!variable.Surfaces.includes(surface)) continue
        tags[variable.Name] = {
            // Server-supplied label. Unlayer's menu is compact, so the short label beats the
            // prose Description here (the reference panel takes the opposite trade).
            name: variable.Label,
            value: variable.Token,
            // Unlayer renders `sample` in its preview mode. An unavailable variable has no
            // value to show, so give the reason rather than a misleading blank.
            sample: variable.SampleValue ?? variable.UnavailableReason ?? 'Not set for this web',
        }
    }

    return tags
}
